import { useState } from "react";
import SEOHead from "@/components/base/SEOHead";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get("phone_alt") as string;
    if (honeypot && honeypot.trim() !== "") {
      setFormStatus("success");
      return;
    }

    fetch("https://form.readdy.ai/api/submit", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams(
        Array.from(formData.entries())
          .filter(([key]) => key !== "phone_alt")
          .map(([k, v]) => [k, v.toString()])
      ).toString(),
    })
      .then(async (res) => {
        const text = await res.text();
        let parsed;
        try { parsed = JSON.parse(text); } catch { parsed = null; }
        const serverMsg = parsed?.meta?.message || parsed?.message || text;
        if (res.ok && parsed?.code === "OK") {
          setFormStatus("success");
          form.reset();
        } else {
          setFormStatus("error");
          setFormError(serverMsg || "提交失败，请稍后再试");
        }
      })
      .catch(() => {
        setFormStatus("error");
        setFormError("网络错误，请稍后再试");
      });
  };

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于蘑菇视频",
    description: "蘑菇视频是专业的视频播放器APP开发商，提供91视频、91在线视频等平台的官方APP下载服务。支持安卓、苹果、iPad全平台覆盖。",
    url: "https://www.moguvideo.com/about",
  };

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "蘑菇视频",
    url: "https://www.moguvideo.com",
    description: "专业视频播放器APP开发商，提供安卓、苹果、iPad全平台视频播放解决方案。",
    foundingDate: "2020",
    contactPoint: {
      "@type": "ContactPoint",
      email: "support@moguvideo.com",
      telephone: "+86-400-123-4567",
      contactType: "customer service",
      availableLanguage: ["Chinese"],
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      { "@type": "Question", name: "蘑菇视频APP是否免费？", acceptedAnswer: { "@type": "Answer", text: "是的，蘑菇视频APP完全免费下载和使用，基础功能全部免费开放。" } },
      { "@type": "Question", name: "蘑菇视频支持哪些设备？", acceptedAnswer: { "@type": "Answer", text: "蘑菇视频支持安卓手机、苹果iPhone、iPad等主流设备，全平台覆盖。" } },
    ],
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 蘑菇视频品牌介绍与联系方式"
        description="蘑菇视频品牌介绍：专业的视频播放器APP开发商。了解我们的品牌故事、用户口碑评价和联系方式。支持安卓苹果iPad全平台。"
        keywords="蘑菇视频,关于我们,品牌介绍,联系方式,视频播放器"
        canonicalUrl="https://www.moguvideo.com/about"
        schemaData={{ ...aboutSchema, ...orgSchema, ...faqSchema }}
      />
      <Navbar />

      <main className="pt-16">
        <Breadcrumb items={[{ label: "关于我们" }]} />

        {/* Brand Intro */}
        <section className="w-full bg-background-50 py-14 md:py-18">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <span className="text-xs font-semibold text-primary-500 uppercase tracking-wider mb-3 block">关于蘑菇视频</span>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-5 leading-tight" style={{ fontFamily: "var(--font-heading)" }}>
                  让每一次观影
                  <br />
                  都成为享受
                </h1>
                <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                  <p>
                    蘑菇视频成立于2020年，是一家专注于移动视频播放技术研发的互联网科技公司。我们致力于为广大用户提供最优质的移动视频观看体验，让每个人都能随时随地享受高清流畅的视频内容。
                  </p>
                  <p>
                    经过多年的技术积累和产品迭代，蘑菇视频已经发展成为国内领先的视频播放器APP之一，累计服务超过<strong>5000万用户</strong>，在各大应用市场评分高达<strong>4.9分</strong>。
                  </p>
                  <p>
                    我们的播放引擎采用自主研发的智能解码和P2P加速技术，支持最高4K超清画质，在网络环境不佳的情况下也能保持流畅播放。同时，我们严格遵守数据安全规范，保障每一位用户的隐私安全。
                  </p>
                </div>
              </div>
              <div className="rounded-2xl overflow-hidden aspect-[4/3]">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20tech%20company%20office%20environment%20with%20warm%20coral%20and%20cream%20interior%20design%2C%20minimalist%20workspace%2C%20team%20collaboration%2C%20soft%20natural%20lighting%2C%20clean%20aesthetic%2C%20cinematic%20atmosphere&width=800&height=600&seq=about-brand&orientation=landscape"
                  alt="蘑菇视频团队办公环境"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Mission & Values */}
        <section className="w-full bg-background-100 py-14 md:py-18">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4" style={{ fontFamily: "var(--font-heading)" }}>
                我们的使命与价值观
              </h2>
              <p className="text-sm text-foreground-600 max-w-[560px] mx-auto">
                以用户为中心，用技术创造价值
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { icon: "ri-focus-3-line", title: "用户至上", desc: "一切以用户体验为核心，倾听用户声音，持续优化产品，让每一位用户满意。" },
                { icon: "ri-code-box-line", title: "技术创新", desc: "坚持自主研发核心技术，不断突破播放技术边界，保持行业领先地位。" },
                { icon: "ri-shield-star-line", title: "安全可靠", desc: "将用户数据安全放在首位，严格遵守隐私保护规范，让用户放心使用。" },
                { icon: "ri-team-line", title: "合作共赢", desc: "与内容平台紧密合作，构建健康的视频生态，实现多方共赢发展。" },
              ].map((item, i) => (
                <div key={i} className="bg-background-50 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-accent-100 flex items-center justify-center">
                    <i className={`${item.icon} text-accent-500 text-xl`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950 mb-2">{item.title}</h3>
                  <p className="text-xs text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reputation / Testimonials */}
        <section className="w-full bg-background-50 py-14 md:py-18">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4" style={{ fontFamily: "var(--font-heading)" }}>
                用户口碑
              </h2>
              <p className="text-sm text-foreground-600">听听真实用户怎么说</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                { name: "王先生", role: "影视爱好者", rating: 5, text: "蘑菇视频是我用过最好用的播放器，画质清晰、播放流畅，资源也很丰富。投屏功能特别实用，晚上在家用电视看大片体验超棒！" },
                { name: "李女士", role: "追剧达人", rating: 5, text: "追剧必备APP！更新快、分类全，离线下载功能通勤路上太方便了。界面简洁好操作，推荐给身边所有追剧的朋友了。" },
                { name: "张先生", role: "数码玩家", rating: 5, text: "从技术角度来说，蘑菇视频的播放引擎确实做得好。4K视频秒加载，解码效率高，iPad上看效果惊艳。团队技术实力值得信赖。" },
              ].map((review, i) => (
                <div key={i} className="bg-background-100 rounded-xl p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: review.rating }).map((_, j) => (
                      <i key={j} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    {review.text}
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-secondary-200 flex items-center justify-center">
                      <span className="text-xs font-semibold text-secondary-700">{review.name[0]}</span>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-950">{review.name}</div>
                      <div className="text-xs text-foreground-500">{review.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="w-full bg-background-100 py-14 md:py-18">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-5" style={{ fontFamily: "var(--font-heading)" }}>
                  联系我们
                </h2>
                <p className="text-sm text-foreground-600 mb-8 leading-relaxed">
                  如有任何问题或合作意向，欢迎通过以下方式联系我们。我们的客服团队将在24小时内回复。
                </p>

                <div className="space-y-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-500 text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-950 mb-0.5">电子邮箱</div>
                      <div className="text-sm text-foreground-600">support@moguvideo.com</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-primary-500 text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-950 mb-0.5">客服电话</div>
                      <div className="text-sm text-foreground-600">400-123-4567</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-primary-500 text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-950 mb-0.5">工作时间</div>
                      <div className="text-sm text-foreground-600">周一至周日 9:00 - 21:00</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-primary-500 text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-950 mb-0.5">公司地址</div>
                      <div className="text-sm text-foreground-600">广东省深圳市南山区科技园南路888号</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-background-50 rounded-2xl p-6 md:p-8">
                <h3 className="text-lg font-semibold text-foreground-950 mb-5" style={{ fontFamily: "var(--font-heading)" }}>
                  发送消息
                </h3>

                {formStatus === "success" ? (
                  <div className="text-center py-8">
                    <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-accent-100 flex items-center justify-center">
                      <i className="ri-check-line text-accent-500 text-2xl"></i>
                    </div>
                    <h4 className="text-base font-semibold text-foreground-950 mb-2">提交成功</h4>
                    <p className="text-sm text-foreground-600">感谢您的留言，我们将在24小时内回复。</p>
                    <button
                      onClick={() => setFormStatus("idle")}
                      className="mt-5 text-sm text-primary-500 hover:text-primary-600 cursor-pointer"
                    >
                      发送新消息
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form="">
                    <div>
                      <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-subject" className="block text-xs font-medium text-foreground-700 mb-1.5">主题</label>
                      <input
                        id="contact-subject"
                        name="subject"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                        placeholder="请输入消息主题"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">消息内容</label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                        placeholder="请输入您的消息（500字以内）"
                      ></textarea>
                    </div>

                    {/* Honeypot */}
                    <div className="contact-hp-field">
                      <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    </div>

                    {formStatus === "error" && (
                      <div className="text-sm text-red-500 bg-red-50 rounded-lg px-4 py-2.5">
                        {formError}
                      </div>
                    )}

                    <button
                      type="submit"
                      className="w-full py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      发送消息
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}